import {ReactComponent as IconStatusLoading} from '#/components/icons/ico-status-loading.svg';
import {removeDoubleAndStartingSpaces} from '#/utils/string-utils';
import {DefaultChildren} from '#/utils/utils';
import {FunctionComponent} from 'react';

export type AsyncButtonProps = Pick<
  React.HTMLProps<HTMLButtonElement>,
  'className' | 'name' | 'disabled' | 'onClick'
> & {
  isLoading?: boolean;
  type?: 'button' | 'submit';
  variant?: 'regular' | 'hollow';
  size?: 'xs' | 'regular';
} & DefaultChildren;

const AsyncButton: FunctionComponent<AsyncButtonProps> = ({
  name = '',
  children,
  className,
  isLoading = false,
  disabled,
  type = 'button',
  onClick,
  variant = 'regular',
  size = 'regular',
}) => {
  const bgColor = variant === 'regular' ? 'bg-blue-600' : 'bg-transparent';
  const borderColor = variant === 'regular' ? 'border-blue-600' : 'border-white';
  const padding = size === 'xs' ? 'px-2' : 'p-2 py-3';
  const width = size === 'xs' ? '' : 'w-full';
  const border = size === 'xs' ? 'border' : 'border-2';
  const rounded = size === 'xs' ? 'rounded-lg' : 'rounded-full';

  const buttonClassName = removeDoubleAndStartingSpaces(`
  ${width}
  ${padding}
  ${bgColor}
  ${borderColor}
  ${border}
  ${rounded}
  overflow-hidden
  uppercase
  text-white
  text-sm
  font-bold
  cursor-pointer
  outline-none
  relative
  transition-colors
  hover:opacity-70
  focus-visible:bg-blue-700
  focus-visible:ring
  focus-visible:ring-blue-400
  focus-visible:ring-offset-4
  focus-visible:ring-offset-gray-700
  disabled:bg-gray-700
  disabled:border-gray-700
  disabled:opacity-50
  disabled:cursor-not-allowed
  data-[loading=true]:cursor-wait
  ${className ?? ''}
  `);

  return (
    <button
      className={buttonClassName}
      type={type}
      disabled={disabled || isLoading}
      data-loading={isLoading}
      onClick={onClick}
      name={name}
    >
      <div className={isLoading ? 'opacity-0' : ''}> {children}</div>
      {isLoading && (
        <div className='absolute w-full h-full top-0 left-0 flex justify-center items-center'>
          <IconStatusLoading
            role='status'
            className='w-4 sm:w-7 aspect-square animate-spin text-gray-600 fill-blue-600'
          />
        </div>
      )}
    </button>
  );
};

export default AsyncButton;
